import React, { useRef } from 'react';
import { Zap } from 'lucide-react';
import useArrowNav from '../../hooks/useArrowNav';
import { safeAsync, sp, Badge, emptyMsg, btnPrimary, neuShadow, neuShadowInset, neuShadowSm } from './crmShared';

export default function SOPGroup({
  tab, staff, scorecardMetrics, escalations, pipelineRules, compPlans, compGlobals,
  canCreate, seeding, setSeeding, loadExtended
}) {
  const metricsGridRef = useRef(null);
  const { onKeyDown: handleMetricsKeyDown } = useArrowNav(metricsGridRef, '[data-metric-card]', { horizontal: true, vertical: true });

  const compGridRef = useRef(null);
  const { onKeyDown: handleCompKeyDown } = useArrowNav(compGridRef, '[data-comp-card]', { horizontal: true, vertical: true });
  if (tab === 'team') {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#212529', margin: 0 }}>Team Roles & Daily Operations</h3>
          {canCreate && staff.length === 0 && (
            <button onClick={safeAsync(async () => { setSeeding(true); await window.zignagenixAPI.seedDefaultStaff(); await loadExtended(); setSeeding(false); })}
              disabled={seeding} style={{ ...btnPrimary, background: '#256F7A' }}>
              <Zap size={13} /> Seed Team
            </button>
          )}
        </div>
        {staff.length === 0 ? (
          <div style={emptyMsg}>No team members configured. Seed defaults or add manually.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {staff.map(s => {
              const roleColors = { lead_response: '#2E8B98', pem: '#2E8B98', closer: '#62BB46', admin: '#6C757D' };
              const rc = roleColors[s.role_type] || '#6C757D';
              const stages = sp(s.owned_stages, []);
              const dos = sp(s.capabilities_do, []);
              const donts = sp(s.capabilities_dont, []);
              const sched = sp(s.daily_schedule, []);
              return (
                <div key={s.id} style={{ background: '#F8F9FA', borderRadius: 14, border: 'none', boxShadow: neuShadow, padding: 20, borderLeft: `4px solid ${rc}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div>
                      <span style={{ fontSize: 16, fontWeight: 700, color: '#212529' }}>{s.name}</span>
                      <span style={{ marginLeft: 10 }}><Badge text={s.role_title} color={rc} /></span>
                    </div>
                    <Badge text={(s.role_type || '').replace(/_/g, ' ').toUpperCase()} color={rc} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div style={{ background: '#e8f5e9', borderRadius: 10, padding: 12, boxShadow: neuShadowInset }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', marginBottom: 6 }}>DOES</div>
                      {dos.map((d, i) => <div key={i} style={{ fontSize: 12, color: '#212529', padding: '2px 0' }}>✅ {d}</div>)}
                    </div>
                    <div style={{ background: '#fce8e8', borderRadius: 10, padding: 12, boxShadow: neuShadowInset }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#DC3545', marginBottom: 6 }}>DOES NOT</div>
                      {donts.map((d, i) => <div key={i} style={{ fontSize: 12, color: '#212529', padding: '2px 0' }}>🚫 {d}</div>)}
                    </div>
                  </div>
                  {stages.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 6 }}>OWNS STAGES</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {stages.map((st, i) => <Badge key={i} text={st} color={rc} />)}
                      </div>
                    </div>
                  )}
                  {sched.length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 6 }}>DAILY SCHEDULE</div>
                      {sched.map((bl, i) => (
                        <div key={i} style={{ display: 'flex', gap: 10, padding: '4px 0', fontSize: 12 }}>
                          <span style={{ fontWeight: 600, color: '#212529', minWidth: 100 }}>{bl.time}</span>
                          <span style={{ color: '#6C757D' }}>{bl.tasks}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (tab === 'scorecard') {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#212529', margin: 0 }}>Performance Scorecard</h3>
          {canCreate && scorecardMetrics.length === 0 && (
            <button onClick={safeAsync(async () => { setSeeding(true); await window.zignagenixAPI.seedDefaultScorecard(); await loadExtended(); setSeeding(false); })}
              disabled={seeding} style={{ ...btnPrimary, background: '#256F7A' }}>
              <Zap size={13} /> Seed Metrics
            </button>
          )}
        </div>
        <p style={{ fontSize: 12, color: '#6C757D', marginBottom: 16, fontStyle: 'italic' }}>
          Reviewed DAILY (Manager) + WEEKLY (Team) — Data &gt; Opinions — No defending numbers, fix them.
        </p>
        {scorecardMetrics.length === 0 ? (
          <div style={emptyMsg}>No scorecard metrics configured. Seed defaults to get started.</div>
        ) : (
          (() => {
            const groups = { lead_response: 'Maria — Lead Response & Logistics', pem: 'Luisa & Ryland — Qualification / Booking', closer: 'Manny & Anna — Closers', admin: 'Doctor Utilization (Admin Metric)' };
            const groupColors = { lead_response: '#2E8B98', pem: '#2E8B98', closer: '#62BB46', admin: '#6C757D' };
            return Object.entries(groups).map(([roleType, groupLabel]) => {
              const metrics = scorecardMetrics.filter(m => m.role_type === roleType);
              if (metrics.length === 0) return null;
              return (
                <div key={roleType} style={{ background: '#F8F9FA', borderRadius: 14, border: 'none', boxShadow: neuShadow, padding: 20, marginBottom: 16, borderLeft: `4px solid ${groupColors[roleType]}` }}>
                  <h4 style={{ fontSize: 14, fontWeight: 700, color: '#212529', margin: '0 0 12px' }}>{groupLabel}</h4>
                  <div ref={metricsGridRef} onKeyDown={handleMetricsKeyDown} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                    {metrics.map(m => {
                      const dirSymbol = m.target_direction === 'lte' ? '≤' : m.target_direction === 'gte' ? '≥' : '=';
                      return (
                        <div key={m.id} data-metric-card tabIndex={0} style={{ background: '#F8F9FA', borderRadius: 10, padding: 12, boxShadow: neuShadowInset, outline: 'none' }}
                          onFocus={(e) => { e.currentTarget.style.boxShadow = `${neuShadowInset}, 0 0 0 2px ${groupColors[roleType]}`; }}
                          onBlur={(e) => { e.currentTarget.style.boxShadow = neuShadowInset; }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#212529', marginBottom: 4 }}>{m.metric_name}</div>
                          <div style={{ fontSize: 18, fontWeight: 700, color: groupColors[roleType] }}>
                            {dirSymbol} {m.target_value}{m.target_unit === '%' ? '%' : m.target_unit === 'min' ? ' min' : m.target_unit === 'hrs' ? ' hrs' : m.target_unit === 'count' ? '' : ` ${m.target_unit}`}
                          </div>
                          {m.failure_note && <div style={{ fontSize: 10, color: '#DC3545', marginTop: 4, fontStyle: 'italic' }}>{m.failure_note}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()
        )}
      </div>
    );
  }

  if (tab === 'escalations') {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#212529', margin: 0 }}>Escalation Rules</h3>
          {canCreate && escalations.length === 0 && (
            <button onClick={safeAsync(async () => { setSeeding(true); await window.zignagenixAPI.seedDefaultEscalations(); await loadExtended(); setSeeding(false); })}
              disabled={seeding} style={{ ...btnPrimary, background: '#DC3545' }}>
              <Zap size={13} /> Seed Escalations
            </button>
          )}
        </div>
        {escalations.length === 0 ? (
          <div style={emptyMsg}>No escalation rules configured.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {escalations.map(e => {
              const cc = sp(e.condition_config, {});
              const ac = sp(e.action_config, {});
              return (
                <div key={e.id} style={{ background: '#F8F9FA', borderRadius: 14, border: 'none', boxShadow: neuShadow, padding: 16, borderLeft: '4px solid #DC3545' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#212529' }}>{e.name}</span>
                    <Badge text={e.is_active ? 'Active' : 'Inactive'} color={e.is_active ? '#62BB46' : '#F26522'} />
                  </div>
                  {e.description && <p style={{ fontSize: 12, color: '#6C757D', margin: '0 0 8px' }}>{e.description}</p>}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div style={{ background: '#fce8e8', borderRadius: 10, padding: 10, boxShadow: neuShadowInset }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#DC3545', marginBottom: 4 }}>CONDITION</div>
                      <div style={{ fontSize: 12, color: '#212529' }}>
                        {e.condition_type === 'time_in_stage' && `Stage "${cc.stage}" > ${cc.threshold_minutes >= 60 ? `${Math.round(cc.threshold_minutes / 60)} hr(s)` : `${cc.threshold_minutes} min`}`}
                      </div>
                    </div>
                    <div style={{ background: '#e3f0fa', borderRadius: 10, padding: 10, boxShadow: neuShadowInset }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#0369a1', marginBottom: 4 }}>ACTION</div>
                      <div style={{ fontSize: 12, color: '#212529' }}>
                        {e.action_type === 'escalate' && `Notify ${ac.notify}: "${ac.message}"`}
                        {e.action_type === 'move_stage' && `Move → ${ac.target_stage}${ac.add_tag ? ` + Tag: ${ac.add_tag}` : ''}${ac.final_attempt ? ' (final attempt)' : ''}`}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pipeline Rules */}
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#212529', margin: 0 }}>Pipeline Rules (No Exceptions)</h3>
            {canCreate && pipelineRules.length === 0 && (
              <button onClick={safeAsync(async () => { setSeeding(true); await window.zignagenixAPI.seedDefaultPipelineRules(); await loadExtended(); setSeeding(false); })}
                disabled={seeding} style={{ ...btnPrimary, background: '#F26522' }}>
                <Zap size={13} /> Seed Rules
              </button>
            )}
          </div>
          {pipelineRules.length === 0 ? (
            <div style={emptyMsg}>No pipeline rules configured.</div>
          ) : (
            <div style={{ background: '#F8F9FA', borderRadius: 14, border: 'none', boxShadow: neuShadow, padding: 16 }}>
              {pipelineRules.map((r, i) => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < pipelineRules.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#F26522', minWidth: 24 }}>{r.rule_order}.</span>
                  <span style={{ fontSize: 13, color: '#212529', fontWeight: 500 }}>{r.rule_text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (tab === 'comp') {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#212529', margin: 0 }}>ZignaGenix Compensation Plan</h3>
          {canCreate && compPlans.length === 0 && (
            <button onClick={safeAsync(async () => { setSeeding(true); await window.zignagenixAPI.seedDefaultComp(); await loadExtended(); setSeeding(false); })}
              disabled={seeding} style={{ ...btnPrimary, background: '#62BB46' }}>
              <Zap size={13} /> Seed Compensation
            </button>
          )}
        </div>

        {/* Global Cap Banner */}
        {(() => {
          const capRule = compGlobals.find(g => g.rule_key === 'total_cap_pct');
          return (
            <div style={{ background: 'linear-gradient(135deg, #212529, #334155)', borderRadius: 12, padding: 20, marginBottom: 20, color: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: '#ADB5BD', textTransform: 'uppercase', letterSpacing: 1 }}>Global Rule</div>
                  <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }}>Total Commissions Capped at ≤ {capRule?.rule_value || '7.0'}% of Collected Revenue</div>
                  <div style={{ fontSize: 12, color: '#ADB5BD', marginTop: 4 }}>Allocated by stage ownership, not by favoritism. No overrides. No stacking.</div>
                </div>
                <div style={{ fontSize: 36, fontWeight: 800, color: '#62BB46' }}>{capRule?.rule_value || '7.0'}%</div>
              </div>
            </div>
          );
        })()}

        {/* Pool Allocation Table */}
        <div style={{ background: '#F8F9FA', borderRadius: 14, border: 'none', boxShadow: neuShadow, padding: 20, marginBottom: 20 }}>
          <h4 style={{ fontSize: 13, fontWeight: 700, color: '#212529', margin: '0 0 12px' }}>7% Commission Pool Allocation (Fixed)</h4>
          <div style={{ borderRadius: 12, overflow: 'hidden', boxShadow: neuShadow }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', background: '#f1f5f9', padding: '8px 16px', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
              <span>Role Group</span><span style={{ textAlign: 'right' }}>% of Total Sales</span>
            </div>
            {compPlans.sort((a, b) => b.pool_pct - a.pool_pct).map(p => {
              const colors = { closer: '#62BB46', pem: '#2E8B98', lead_response: '#2E8B98' };
              return (
                <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', padding: '10px 16px', borderTop: '1px solid #f1f5f9', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#212529' }}>{p.role_label}</span>
                  <span style={{ textAlign: 'right', fontSize: 16, fontWeight: 700, color: colors[p.role_type] || '#6C757D' }}>{p.pool_pct}%</span>
                </div>
              );
            })}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', padding: '10px 16px', borderTop: '2px solid #212529', background: '#e0e5ec' }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: '#212529' }}>TOTAL</span>
              <span style={{ textAlign: 'right', fontSize: 16, fontWeight: 800, color: '#212529' }}>7.0% (Hard Cap)</span>
            </div>
          </div>
        </div>

        {/* Individual Plan Cards */}
        {compPlans.length === 0 ? (
          <div style={emptyMsg}>No compensation plans configured. Seed defaults to get started.</div>
        ) : (
          <div ref={compGridRef} onKeyDown={handleCompKeyDown} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {compPlans.sort((a, b) => b.pool_pct - a.pool_pct).map(plan => {
              const colors = { closer: '#62BB46', pem: '#2E8B98', lead_response: '#2E8B98' };
              const rc = colors[plan.role_type] || '#6C757D';
              const structure = sp(plan.comp_structure, []);
              const disqs = sp(plan.disqualifiers, []);
              const gates = sp(plan.quality_gates, []);
              const kicker = sp(plan.kicker_config, null);
              return (
                <div key={plan.id} data-comp-card tabIndex={0} style={{ background: '#F8F9FA', borderRadius: 14, border: 'none', boxShadow: neuShadow, padding: 24, borderLeft: `4px solid ${rc}`, outline: 'none' }}
                  onFocus={(e) => { e.currentTarget.style.boxShadow = `${neuShadow}, 0 0 0 2px ${rc}`; }}
                  onBlur={(e) => { e.currentTarget.style.boxShadow = neuShadow; }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <h4 style={{ fontSize: 15, fontWeight: 700, color: '#212529', margin: 0 }}>{plan.role_label}</h4>
                    <span style={{ fontSize: 24, fontWeight: 800, color: rc }}>{plan.pool_pct}%</span>
                  </div>
                  <p style={{ fontSize: 12, color: '#6C757D', margin: '0 0 16px', fontStyle: 'italic' }}>{plan.description}</p>

                  {/* Compensation Structure */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#6C757D', textTransform: 'uppercase', marginBottom: 8 }}>Compensation Structure</div>
                    {structure.map((s, i) => (
                      <div key={i} style={{ background: '#F8F9FA', borderRadius: 10, padding: 12, marginBottom: 6, boxShadow: neuShadowSm }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: rc }}>{s.tier}</div>
                        <div style={{ fontSize: 12, color: '#212529', marginTop: 2 }}>{s.condition}</div>
                        <div style={{ fontSize: 11, color: '#6C757D', marginTop: 2 }}>Timing: {s.timing}</div>
                      </div>
                    ))}
                  </div>

                  {/* Kicker */}
                  {kicker && (
                    <div style={{ marginBottom: 16, background: '#f0fdf4', borderRadius: 8, padding: 14, border: 'none', boxShadow: neuShadowSm }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', marginBottom: 8 }}>Performance Kicker ({kicker.pool_pct}%)</div>
                      {kicker.conditions.map((c, i) => (
                        <div key={i} style={{ fontSize: 12, color: '#212529', padding: '2px 0' }}>✅ {c.metric}: {c.target}</div>
                      ))}
                      <div style={{ fontSize: 11, color: '#DC3545', fontWeight: 600, marginTop: 6 }}>{kicker.rule}</div>
                    </div>
                  )}

                  {/* Quality Gates */}
                  {gates.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#6C757D', textTransform: 'uppercase', marginBottom: 6 }}>Quality Gates</div>
                      {gates.map((g, i) => <div key={i} style={{ fontSize: 12, color: '#212529', padding: '2px 0' }}>🔒 {g}</div>)}
                    </div>
                  )}

                  {/* Disqualifiers */}
                  {disqs.length > 0 && (
                    <div style={{ background: '#fef2f2', borderRadius: 8, padding: 14, border: 'none', boxShadow: neuShadowSm }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#DC3545', textTransform: 'uppercase', marginBottom: 6 }}>Disqualifiers — Earns $0 on Case If:</div>
                      {disqs.map((d, i) => <div key={i} style={{ fontSize: 12, color: '#212529', padding: '2px 0' }}>🚫 {d}</div>)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Global Safety Controls */}
        {compGlobals.length > 0 && (
          <div style={{ background: '#F8F9FA', borderRadius: 14, border: 'none', boxShadow: neuShadow, padding: 20, marginTop: 20, borderLeft: '4px solid #DC3545' }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#DC3545', margin: '0 0 12px' }}>Global Safety Controls (Non-Negotiable)</h4>
            {compGlobals.map(g => (
              <div key={g.id} style={{ display: 'flex', gap: 10, padding: '6px 0', borderBottom: '1px solid #f8fafc', fontSize: 12 }}>
                <span style={{ fontWeight: 600, color: '#212529', minWidth: 160 }}>{g.rule_key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                <span style={{ color: '#6C757D' }}>{g.description}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
}
